import { useMemo, useState } from "react";
import {
  oawTriangle,
  plotToXY,
  relativeMotionFromPlots,
  type Plot,
} from "./plotMath.js";

const DEFAULT_PLOTS: Plot[] = [];

/**
 * Plotting workspace: students mark a series of (bearing,range,time) fixes
 * for one or more targets, see the geometry on a digital plotting sheet,
 * and read the relative-motion vector + CPA + TCPA computed automatically
 * from the most recent two fixes per target. An OAW triangle calculator
 * sits alongside for sailing-vessel students.
 *
 * Pure presentation; the maths lives in `plotMath.ts` and is unit-tested.
 */
export function PlottingSheet(): JSX.Element {
  const [plots, setPlots] = useState<Plot[]>(DEFAULT_PLOTS);

  const grouped = useMemo(() => {
    const map = new Map<string, Plot[]>();
    for (const p of plots) {
      const list = map.get(p.targetId) ?? [];
      list.push(p);
      map.set(p.targetId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.simSeconds - b.simSeconds);
    }
    return map;
  }, [plots]);

  const addPlot = (p: Plot) => setPlots((prev) => [...prev, p]);
  const clear = () => setPlots([]);

  return (
    <div
      style={{
        color: "var(--text)",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 340px",
        gap: 16,
        width: "100%",
        maxWidth: 1280,
      }}
    >
      <section style={panel}>
        <h2 style={h2}>Plotting sheet</h2>
        <Sheet plots={plots} grouped={grouped} />
      </section>
      <aside style={{ ...panel, gap: 18 }}>
        <PlotForm onAdd={addPlot} />
        <RelativeMotionPanel grouped={grouped} />
        <OAWPanel />
        <button
          onClick={clear}
          style={{
            ...button,
            background: "transparent",
            color: "var(--danger)",
            border: "1px solid var(--danger)",
          }}
        >
          Clear all plots
        </button>
      </aside>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const h2: React.CSSProperties = {
  fontSize: 14,
  margin: 0,
  fontWeight: 600,
  color: "var(--text)",
};

const button: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-fg)",
  border: "1px solid var(--accent)",
  borderRadius: "var(--radius-sm)",
  padding: "8px 14px",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const inputStyle: React.CSSProperties = {
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  borderRadius: "var(--radius-sm)",
  padding: "7px 10px",
  fontFamily: "inherit",
  fontSize: 13,
  width: "100%",
};

const SHEET_RANGE_NM = 12;
const SHEET_PX = 600;

function Sheet(props: {
  plots: Plot[];
  grouped: Map<string, Plot[]>;
}): JSX.Element {
  const cx = SHEET_PX / 2;
  const cy = SHEET_PX / 2;
  const radiusPx = SHEET_PX / 2 - 30;
  const pxPerNm = radiusPx / SHEET_RANGE_NM;

  const project = (p: Pick<Plot, "bearingDeg" | "rangeNm">) => {
    const xy = plotToXY(p);
    return { x: cx + xy.x * pxPerNm, y: cy - xy.y * pxPerNm };
  };

  return (
    <svg
      width={SHEET_PX}
      height={SHEET_PX}
      viewBox={`0 0 ${SHEET_PX} ${SHEET_PX}`}
      style={{
        background: "#020a08",
        borderRadius: "var(--radius-md)",
        alignSelf: "center",
        border: "1px solid var(--border)",
      }}
    >
      {[3, 6, 9, 12].map((r) => (
        <circle
          key={r}
          cx={cx}
          cy={cy}
          r={r * pxPerNm}
          fill="none"
          stroke="rgba(120, 220, 160, 0.25)"
        />
      ))}
      {[3, 6, 9, 12].map((r) => (
        <text
          key={`l-${r}`}
          x={cx + 6}
          y={cy - r * pxPerNm + 4}
          fontSize={10}
          fill="rgba(180, 240, 200, 0.6)"
        >
          {r} nm
        </text>
      ))}
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((b) => {
        const rad = (b * Math.PI) / 180;
        const x2 = cx + radiusPx * Math.sin(rad);
        const y2 = cy - radiusPx * Math.cos(rad);
        return (
          <line
            key={b}
            x1={cx}
            y1={cy}
            x2={x2}
            y2={y2}
            stroke="rgba(120, 220, 160, 0.15)"
          />
        );
      })}
      {[0, 90, 180, 270].map((b) => {
        const rad = (b * Math.PI) / 180;
        const x = cx + (radiusPx + 12) * Math.sin(rad);
        const y = cy - (radiusPx + 12) * Math.cos(rad);
        return (
          <text
            key={`b-${b}`}
            x={x}
            y={y}
            fontSize={11}
            fill="rgba(180, 240, 200, 0.8)"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {String(b).padStart(3, "0")}°
          </text>
        );
      })}
      <circle cx={cx} cy={cy} r={4} fill="#cbe8d0" />
      <text
        x={cx + 8}
        y={cy + 14}
        fontSize={10}
        fill="rgba(180, 240, 200, 0.8)"
      >
        OWN
      </text>
      {[...props.grouped.entries()].map(([targetId, list]) => {
        const colour = colourFor(targetId);
        const xy = list.map(project);
        return (
          <g key={targetId}>
            {xy.length >= 2 && (
              <line
                x1={xy[0]!.x}
                y1={xy[0]!.y}
                x2={xy[xy.length - 1]!.x}
                y2={xy[xy.length - 1]!.y}
                stroke={colour}
                strokeWidth={1.5}
                strokeDasharray="4 4"
              />
            )}
            {list.map((p, i) => (
              <g key={`${targetId}-${i}`}>
                <circle cx={xy[i]!.x} cy={xy[i]!.y} r={4} fill={colour} />
                <text
                  x={xy[i]!.x + 6}
                  y={xy[i]!.y - 6}
                  fontSize={10}
                  fill={colour}
                >
                  {p.label}
                </text>
              </g>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function PlotForm(props: { onAdd: (p: Plot) => void }): JSX.Element {
  const [targetId, setTargetId] = useState("T");
  const [label, setLabel] = useState("1");
  const [bearingDeg, setBearingDeg] = useState("90");
  const [rangeNm, setRangeNm] = useState("4");
  const [simSeconds, setSimSeconds] = useState("0");

  const submit = () => {
    const b = parseFloat(bearingDeg);
    const r = parseFloat(rangeNm);
    const t = parseFloat(simSeconds);
    if (!Number.isFinite(b) || !Number.isFinite(r) || !Number.isFinite(t)) return;
    if (r < 0 || r > 24 || b < 0 || b >= 360) return;
    props.onAdd({
      targetId: targetId.trim() || "T",
      label: label.trim() || "1",
      bearingDeg: b,
      rangeNm: r,
      simSeconds: t,
    });
    // Auto-increment label for the next plot of the same target.
    const numeric = parseInt(label, 10);
    if (!Number.isNaN(numeric)) setLabel(String(numeric + 1));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h2 style={h2}>Add plot</h2>
      <Field label="Target id">
        <input
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Label">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Bearing °T">
        <input
          value={bearingDeg}
          onChange={(e) => setBearingDeg(e.target.value)}
          style={inputStyle}
          inputMode="decimal"
        />
      </Field>
      <Field label="Range (nm)">
        <input
          value={rangeNm}
          onChange={(e) => setRangeNm(e.target.value)}
          style={inputStyle}
          inputMode="decimal"
        />
      </Field>
      <Field label="Sim seconds">
        <input
          value={simSeconds}
          onChange={(e) => setSimSeconds(e.target.value)}
          style={inputStyle}
          inputMode="numeric"
        />
      </Field>
      <button onClick={submit} style={button}>
        Add plot
      </button>
    </div>
  );
}

function Field(props: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 10, opacity: 0.7 }}>{props.label}</span>
      {props.children}
    </label>
  );
}

function RelativeMotionPanel(props: {
  grouped: Map<string, Plot[]>;
}): JSX.Element {
  const items: { id: string; rm: ReturnType<typeof relativeMotionFromPlots> }[] = [];
  for (const [id, list] of props.grouped) {
    if (list.length < 2) continue;
    const last = list[list.length - 1]!;
    const prev = list[list.length - 2]!;
    try {
      items.push({ id, rm: relativeMotionFromPlots(prev, last) });
    } catch {
      // Skip targets where the two plots share a timestamp.
    }
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h2 style={h2}>Relative motion / CPA</h2>
      {items.length === 0 ? (
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Add at least two plots of the same target.
        </span>
      ) : (
        items.map((it) => (
          <div
            key={it.id}
            style={{
              fontSize: 12,
              lineHeight: 1.6,
              padding: "10px 12px",
              background: "var(--bg-elev)",
              borderRadius: "var(--radius-sm)",
              borderLeft: `3px solid ${colourFor(it.id)}`,
            }}
          >
            <strong>{it.id}</strong>
            <div>
              Rel course {it.rm.relativeCourseDeg.toFixed(0).padStart(3, "0")}
              ° at {it.rm.relativeSpeedKnots.toFixed(1)} kn
            </div>
            <div>
              CPA {it.rm.cpaNm.toFixed(2)} nm · TCPA{" "}
              {Number.isFinite(it.rm.tcpaMin)
                ? it.rm.tcpaMin.toFixed(1) + " min"
                : "∞"}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function OAWPanel(): JSX.Element {
  const [ownCourseDeg, setOwnCourseDeg] = useState("90");
  const [ownSpeedKnots, setOwnSpeedKnots] = useState("6");
  const [trueWindFromDeg, setTrueWindFromDeg] = useState("220");
  const [trueWindSpeedKnots, setTrueWindSpeedKnots] = useState("16");

  const out = useMemo(() => {
    try {
      return oawTriangle({
        ownCourseDeg: parseFloat(ownCourseDeg),
        ownSpeedKnots: parseFloat(ownSpeedKnots),
        trueWindFromDeg: parseFloat(trueWindFromDeg),
        trueWindSpeedKnots: parseFloat(trueWindSpeedKnots),
      });
    } catch {
      return null;
    }
  }, [ownCourseDeg, ownSpeedKnots, trueWindFromDeg, trueWindSpeedKnots]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <h2 style={h2}>OAW triangle</h2>
      <Field label="Own course °T">
        <input
          value={ownCourseDeg}
          onChange={(e) => setOwnCourseDeg(e.target.value)}
          style={inputStyle}
          inputMode="decimal"
        />
      </Field>
      <Field label="Own speed (kn)">
        <input
          value={ownSpeedKnots}
          onChange={(e) => setOwnSpeedKnots(e.target.value)}
          style={inputStyle}
          inputMode="decimal"
        />
      </Field>
      <Field label="True wind FROM °T">
        <input
          value={trueWindFromDeg}
          onChange={(e) => setTrueWindFromDeg(e.target.value)}
          style={inputStyle}
          inputMode="decimal"
        />
      </Field>
      <Field label="True wind speed (kn)">
        <input
          value={trueWindSpeedKnots}
          onChange={(e) => setTrueWindSpeedKnots(e.target.value)}
          style={inputStyle}
          inputMode="decimal"
        />
      </Field>
      {out !== null && Number.isFinite(out.apparentWindFromDeg) ? (
        <div
          style={{
            fontSize: 13,
            padding: "10px 12px",
            background: "var(--bg-elev)",
            borderRadius: "var(--radius-sm)",
            color: "var(--text)",
          }}
        >
          Apparent wind FROM{" "}
          {out.apparentWindFromDeg.toFixed(0).padStart(3, "0")}° at{" "}
          {out.apparentWindSpeedKnots.toFixed(1)} kn
        </div>
      ) : null}
    </div>
  );
}

const COLOURS = [
  "#9bd9a4",
  "#e6c46d",
  "#7ec5e0",
  "#e88f7a",
  "#c39df0",
  "#90d6c0",
];

function colourFor(targetId: string): string {
  let h = 0;
  for (let i = 0; i < targetId.length; i++) {
    h = (h + targetId.charCodeAt(i)) | 0;
  }
  return COLOURS[Math.abs(h) % COLOURS.length]!;
}
